import { useEffect, useRef } from 'react';
import { usePageGuideStore } from '../stores/pageGuideStore';
import { useUserStore } from '../stores/userStore';
import { useFoundationGuide } from '../stores/foundationGuideStore';
import { shouldAutoStart, type PageGuide } from '../utils/pageGuide';
import type { GuideHooks } from '../stores/pageGuideStore';

/**
 * Another guidance layer is on screen — a modal dialog or the Foundation guide bar.
 * ⚠️ Not the guide's OWN card (it is `aria-modal` too): it lingers a frame after a
 * guide or the pointer ends, and would hold the next chapter back as "a dialog".
 */
export function otherGuidanceOpen(): boolean {
  if (useFoundationGuide.getState().active) return true;
  return Array.from(document.querySelectorAll('[aria-modal="true"]')).some(el => !el.closest('[data-guide-layer]'));
}

const SETTLE_MS = 600;
const POLL_MS = 500;
const GIVE_UP_MS = 20000;

/**
 * ⭐ A page (or a tab inside it) declares its guide: "?" can then replay it, and it
 * runs by itself the first time (owner, 2026-10-10).
 *
 * ⚠️ Auto-start waits for:
 *  · the account's saved data (`initializedUserId`) — before it, "seen" is unknown
 *    and a returning person would get the guide again; a failed load never starts it;
 *  · the page to settle (layout, data);
 *  · its TURN among the page's guides — a running guide or the closing pointer goes
 *    first, and so does a shallower unseen guide (the overview before a tab's
 *    chapter). It keeps waiting while it is on screen;
 *  · ONE guidance layer at a time — a modal dialog or the Foundation guide bar goes
 *    first; for those it gives up after a while for this visit.
 */
export function usePageGuide(guide: PageGuide, opts: { autoStart?: boolean; enabled?: boolean } & GuideHooks = {}) {
  // `enabled: false` = this page has no guide right now (e.g. a read-only past
  // Life Map — its steps talk about sliders and Save that are not there).
  const enabled = opts.enabled ?? true;
  const autoStart = enabled && (opts.autoStart ?? true);
  const userId = useUserStore(s => s.initializedUserId);
  // ⚠️ Through refs: the store holds ONE stable pair per registration, and each
  // call reads the page's latest state (which domain/tab is open NOW).
  const sceneRef = useRef(opts.onScene);
  const hereRef = useRef(opts.here);
  sceneRef.current = opts.onScene;
  hereRef.current = opts.here;

  useEffect(() => (enabled
    ? usePageGuideStore.getState().register(guide, {
      onScene: scene => sceneRef.current?.(scene),
      here: () => hereRef.current?.() ?? null,
    })
    : undefined), [guide, enabled]);

  useEffect(() => {
    if (!autoStart || !userId) return;
    if (!shouldAutoStart(guide, useUserStore.getState().pageGuidesSeen)) return;
    let timer = 0;
    let blockedSince: number | null = null;
    const retry = () => { timer = window.setTimeout(tryStart, POLL_MS); };
    const tryStart = () => {
      const s = usePageGuideStore.getState();
      if (!s.stack.some(g => g.id === guide.id)) return;
      const seen = useUserStore.getState().pageGuidesSeen;
      if (!shouldAutoStart(guide, seen)) return;
      if (s.active || s.pointer) { retry(); return; }
      const level = guide.level ?? 0;
      if (s.stack.some(g => g.id !== guide.id && (g.level ?? 0) < level && shouldAutoStart(g, seen))) { retry(); return; }
      if (otherGuidanceOpen()) {
        blockedSince ??= Date.now();
        if (Date.now() - blockedSince < GIVE_UP_MS) retry();
        return;
      }
      s.start(guide);
    };
    timer = window.setTimeout(tryStart, SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [guide, autoStart, userId]);
}
